import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, Routes, Route } from 'react-router-dom';
import { Alert, BodyText, Button, formatDateTime } from '@aaimplatform/ui';
import { Info, Save, RotateCcw, Loader2 } from 'lucide-react';
import { AssetClassPoliciesTab } from './AssetClassPoliciesTab';
import { ChaThresholdsTab } from './ChaThresholdsTab';
import { ConcentrationTab } from './ConcentrationTab';
import { MonitoringTab } from './MonitoringTab';
import { AuditTrailTab } from './AuditTrailTab';
import { useCollateralPolicy, composedFromVersion } from '../../hooks/useCollateralPolicy';
import { PolicyVersionConflictError } from '../../api/services/collateral-policy';
import { useInstitutionFlags } from '../../hooks/useInstitutionFlags';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';

type CollateralPolicy = components['schemas']['CollateralPolicy'];
type AssetClassPolicy = components['schemas']['AssetClassPolicy'];
type ConcentrationPolicy = components['schemas']['ConcentrationPolicy'];
type MonitoringPolicy = components['schemas']['MonitoringPolicy'];

/** The category the platform declares every asset-class flag under (feature-flag.service.ts). */
const ASSET_CLASS_FLAG_CATEGORY = 'asset-class';

/**
 * Merges a tab's edited asset classes back into the full list by
 * taxonomyNodeId. The tabs see only the flag-enabled classes; storing their
 * array as the whole list dropped every class whose flag was off from the next
 * save (r3 fi-g7-4).
 */
export function mergeAssetClassPolicies(
  all: AssetClassPolicy[],
  edited: AssetClassPolicy[],
): AssetClassPolicy[] {
  const byId = new Map(edited.map((p) => [p.taxonomyNodeId, p]));
  const merged = all.map((p) => byId.get(p.taxonomyNodeId) ?? p);
  const known = new Set(all.map((p) => p.taxonomyNodeId));
  return [...merged, ...edited.filter((p) => !known.has(p.taxonomyNodeId))];
}

/**
 * Whether the member platform has the saved version (GH #2841): the answer
 * platform-admin-api recorded for the version's push. PENDING means no answer is
 * recorded yet; FAILED means the member platform refused the push or did not
 * answer, so member pledging capacity may still use an earlier version.
 */
function ConsumerSyncStatus({ metadata }: { metadata: CollateralPolicy['metadata'] }) {
  const sync = metadata.consumerSync;
  if (!sync) return null;
  const at = sync.completedAt ? formatDateTime(sync.completedAt) : undefined;
  if (sync.status === 'FAILED') {
    return (
      <Alert type="warning" title={`The member platform did not confirm version ${metadata.version}`} className="mb-6">
        Member pledging capacity may still use an earlier version of this policy.
        {at ? ` Recorded ${at}.` : ''}
      </Alert>
    );
  }
  return (
    <BodyText variant="ui" size="sm" color="tertiary" className="mb-6" role="status">
      {sync.status === 'SUCCEEDED'
        ? `The member platform confirmed version ${metadata.version}${at ? ` on ${at}` : ''}.`
        : `The member platform has not confirmed version ${metadata.version} yet.`}
    </BodyText>
  );
}

/**
 * The editors' Reset and Save row, once for every editable policy route and
 * right-aligned in one place. Each tab placed it in its own header row, so it
 * sat top-right on one page and top-left on the next (r3 fi-g7-11).
 */
function PolicyEditorLayout({ actionBar }: { actionBar: ReactNode }) {
  return (
    <>
      <div className="flex justify-end mb-6">{actionBar}</div>
      <Outlet />
    </>
  );
}

/**
 * CollateralPolicyPage - Server-authoritative collateral policy configuration.
 *
 * Replaces the generic usePlatformConfig pattern with a dedicated
 * useCollateralPolicy hook backed by versioned, audited storage.
 * When a tenant has no saved policy, baselines are loaded as starting config,
 * and every policy route says so once, above the routed page (fi-g7-1).
 *
 * Route: /policy/* (the bare /policy index redirects to /policy/asset-classes,
 * so the nav highlights the page the user is on; fi-g7-4)
 */
export function CollateralPolicyPage() {
  const {
    policy: serverPolicy,
    isLoading,
    error,
    isNewPolicy,
    baselineMissing,
    noTenantSelected,
    save,
    isSaving,
    saveError,
    validate,
    isValidating,
    validationResult,
    auditEntries,
    auditTotal,
    isLoadingAudit,
  } = useCollateralPolicy();

  const { isEnabled, getFlagsByCategory, isLoading: flagsLoading } = useInstitutionFlags();

  const [policy, setPolicy] = useState<CollateralPolicy | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  // False while a whole-number field on the open tab holds an entry that is
  // not valid; Save waits for it (op-08). A tab reports true on unmount.
  const [tabValid, setTabValid] = useState(true);
  const prevPolicyRef = useRef<string | null>(null);

  useEffect(() => {
    if (serverPolicy) {
      const serialized = JSON.stringify(serverPolicy);
      if (serialized !== prevPolicyRef.current) {
        prevPolicyRef.current = serialized;
        setPolicy(serverPolicy);
        setHasChanges(false);
      }
    }
  }, [serverPolicy]);

  const handleAssetClassChange = useCallback((assetClasses: AssetClassPolicy[]) => {
    setPolicy(prev =>
      prev
        ? { ...prev, assetClassPolicies: mergeAssetClassPolicies(prev.assetClassPolicies ?? [], assetClasses) }
        : prev,
    );
    setHasChanges(true);
  }, []);

  const handleConcentrationChange = useCallback((concentration: ConcentrationPolicy) => {
    setPolicy(prev => prev ? { ...prev, concentrationPolicy: concentration } : prev);
    setHasChanges(true);
  }, []);

  const handleMonitoringChange = useCallback((monitoring: MonitoringPolicy) => {
    setPolicy(prev => prev ? { ...prev, monitoringPolicy: monitoring } : prev);
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!policy) return;

    // Run validation before save
    validate({
      assetClassPolicies: policy.assetClassPolicies as unknown as components['schemas']['AssetClassPolicyUpdate'][],
      concentrationPolicy: policy.concentrationPolicy,
      monitoringPolicy: policy.monitoringPolicy,
    }, {
      onSuccess: (result) => {
        if (result.valid) {
          // Unsaved changes clear only when the PUT succeeds. Clearing them
          // beside the call left Save and Reset disabled over unsaved values
          // whenever the PUT failed (fi-g7-3).
          save(
            {
              assetClassPolicies: policy.assetClassPolicies as unknown as components['schemas']['AssetClassPolicyUpdate'][],
              concentrationPolicy: policy.concentrationPolicy,
              monitoringPolicy: policy.monitoringPolicy,
              // The version of the copy these editors were built from. The
              // hook's policy can move to a newer version while validation is
              // in flight (R1c4c-f1).
              baseVersion: composedFromVersion(policy),
            },
            { onSuccess: () => setHasChanges(false) },
          );
        }
      },
    });
  }, [save, validate, policy]);

  const handleReset = useCallback(() => {
    if (serverPolicy) {
      setPolicy(serverPolicy);
      setHasChanges(false);
    }
  }, [serverPolicy]);

  // The platform declares a flag for every asset class, so a read that
  // returns none of them is a failed read, not a platform with no classes:
  // the flag hook answers a failed platform-flag request with an empty list
  // and isEnabled() false for every key (r3 fi-g7-4). The page says so rather
  // than filtering to nothing, and the tabs do not claim none are enabled.
  const flagsUnreadable = !flagsLoading && getFlagsByCategory(ASSET_CLASS_FLAG_CATEGORY).length === 0;

  // Filter asset class policies by feature flags, for display only.
  const filteredPolicies = flagsUnreadable
    ? []
    : policy?.assetClassPolicies?.filter((ac) => {
        const flagKey = `collateral.asset_class.${ac.taxonomyNodeId}`;
        return isEnabled(flagKey);
      }) ?? [];

  const channelState = (key: string): 'available' | 'unavailable' | 'unknown' =>
    flagsUnreadable ? 'unknown' : isEnabled(key) ? 'available' : 'unavailable';
  const channelAvailability = {
    SMS: channelState('collateral.monitoring.SMS'),
    WEBHOOK: channelState('collateral.monitoring.WEBHOOK'),
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-elevated rounded w-1/3" />
        <div className="h-10 bg-elevated rounded" />
        <div className="h-64 bg-elevated rounded" />
      </div>
    );
  }

  // No tenant on the session, or no saved policy and no Aaim baseline for this
  // institution's type (R1c3): there is no policy to show, so no editor renders
  // over empty tabs.
  if (noTenantSelected || baselineMissing) {
    return (
      <div role="note" className="bg-elevated border border-recessed rounded-sm p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5" aria-hidden="true" />
        <BodyText variant="ui" size="sm" color="secondary">
          {noTenantSelected
            ? 'No institution is selected for this session, so there is no collateral policy to show.'
            : 'Aaim defines no baseline collateral policy for your institution’s type, and your institution has not saved a policy, so there is no policy to show.'}
        </BodyText>
      </div>
    );
  }

  // Display names for the audit trail's Section cell, from the policy on screen.
  const classNames = Object.fromEntries(
    (policy?.assetClassPolicies ?? []).map((ac) => [ac.taxonomyNodeId, ac.displayName]),
  );

  const hasViolations = validationResult && !validationResult.valid;
  const hasWarnings = validationResult && validationResult.warnings.length > 0;

  const actionBar = (
    <div className="flex flex-wrap items-center gap-3">
      <Button variant="secondary" onClick={handleReset} disabled={!hasChanges}>
        <RotateCcw className="w-4 h-4 mr-2" />
        Reset
      </Button>
      <Button variant="primary" onClick={handleSave} disabled={!hasChanges || !tabValid || isSaving || isValidating}>
        {(isSaving || isValidating) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Save Changes'}
      </Button>
    </div>
  );

  return (
    <div>
      {/* Baseline notice, once for every policy route. The values on screen are
          Aaim's baseline until the institution saves; without this line the
          editors presented the baseline as the institution's own policy
          (fi-g7-1). Neutral chrome: it is information, not an alert. */}
      {isNewPolicy && (
        <div
          role="note"
          className="bg-elevated border border-recessed rounded-sm p-4 mb-6 flex items-start gap-3"
        >
          <Info className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5" aria-hidden="true" />
          <BodyText variant="ui" size="sm" color="secondary">
            These are Aaim baseline values. Your institution has not saved a policy; saving
            makes them your institution&apos;s policy.
          </BodyText>
        </div>
      )}

      {/* Consumer push status of the saved version (GH #2841). Read from the
          server policy: the editors' local copy carries no metadata changes. */}
      {serverPolicy && !isNewPolicy && <ConsumerSyncStatus metadata={serverPolicy.metadata} />}

      {/* Banners compose from the kit Alert on preset tokens. The prior
          amber and green panels were the default Tailwind palette,
          near-white on the dark theme, and error copy used the CTA red
          (fi-g7-15). */}
      {/* A save another session overtook (R1c4c-f1). The page is not reloaded
          for the user: a reload replaces the editors' unsaved values. */}
      {!error && saveError instanceof PolicyVersionConflictError ? (
        <Alert type="error" title="This policy changed while you were editing" className="mb-6">
          {saveError.activeVersion === null
            ? 'The policy you started from is no longer active. '
            : `Version ${saveError.activeVersion} was saved after you opened this page. `}
          Your changes were not saved. Reload the page to see the current policy, then make your changes again.
        </Alert>
      ) : (error || saveError) && (
        <Alert type="error" className="mb-6">
          {error ? 'Failed to load policy configuration' : 'Failed to save changes'}
        </Alert>
      )}

      {/* Validation violations */}
      {hasViolations && (
        <Alert type="error" title="Policy cannot be saved due to validation violations" className="mb-6">
          <ul className="space-y-1">
            {validationResult.violations.map((v, i) => (
              <li key={i} className="text-sm text-error">
                <span className="font-mono text-xs">{v.field}</span>: {v.message}
                {v.regulatoryBasis && (
                  <span className="text-xs text-tertiary ml-2">({v.regulatoryBasis})</span>
                )}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Validation warnings. This renders from the validate result, which
          arrives before the save resolves and whether or not it succeeds, so
          it reports a validation, never a save (fi-g7-3). */}
      {hasWarnings && !hasViolations && (
        <Alert type="warning" title="Policy validated with warnings" className="mb-6">
          <ul className="space-y-1">
            {validationResult.warnings.map((w, i) => (
              <li key={i} className="text-sm text-secondary">
                <span className="font-mono text-xs">{w.field}</span>: {w.message}
                {w.recommendation && (
                  <span className="text-xs text-tertiary ml-2">{w.recommendation}</span>
                )}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Validation success */}
      {validationResult?.valid && !hasWarnings && (
        <Alert type="success" className="mb-6">
          Policy passes all regulatory guardrails.
        </Alert>
      )}

      {flagsUnreadable && (
        <Alert type="warning" title="Asset-class availability could not be read" className="mb-6">
          The platform asset-class settings did not load, so no asset classes are shown. Saving
          keeps every class already in your policy. Reload the page to try again.
        </Alert>
      )}

      {/* Page content (navigated via left nav Policy group) */}
      <Routes>
        <Route index element={<Navigate to="asset-classes" replace />} />
        <Route element={<PolicyEditorLayout actionBar={actionBar} />}>
          {/* While the platform-flag read is in flight the filtered list is
              empty; the tabs show a loading state rather than claiming no
              class is enabled. */}
          <Route path="asset-classes" element={<AssetClassPoliciesTab policies={filteredPolicies} onChange={handleAssetClassChange} onValidityChange={setTabValid} availabilityUnreadable={flagsUnreadable} availabilityLoading={flagsLoading} />} />
          <Route path="cha-thresholds" element={<ChaThresholdsTab policies={filteredPolicies} onChange={handleAssetClassChange} onValidityChange={setTabValid} availabilityUnreadable={flagsUnreadable} availabilityLoading={flagsLoading} />} />
          <Route path="concentration" element={policy?.concentrationPolicy ? <ConcentrationTab policy={policy.concentrationPolicy} onChange={handleConcentrationChange} /> : null} />
          <Route path="monitoring" element={policy?.monitoringPolicy ? <MonitoringTab policy={policy.monitoringPolicy} onChange={handleMonitoringChange} onValidityChange={setTabValid} channelAvailability={channelAvailability} /> : null} />
        </Route>
        <Route path="audit" element={<AuditTrailTab entries={auditEntries} total={auditTotal} isLoading={isLoadingAudit} classNames={classNames} />} />
      </Routes>
    </div>
  );
}
